import React, { useEffect, useMemo, useState } from 'react';
import { LatLng } from 'leaflet';
import { useParams } from 'react-router';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Box, Checkbox, FormControlLabel, Paper } from '@mui/material';

// Services
import {
  exportProcessedSightingByReportId,
  getProcessedSightingByReportId,
  getProcessedSightingLocationsByReportId,
} from '../../../services/sighting.service';
import { getNearReaders } from '../../../services/reader.service';
import { getGeofences } from '../../../services/geofence.service';
import { getNearFacilities } from '../../../services/facility.service';

// Utils
import { makeReaderReportSightingColumn } from '../../../utils/columns/makeColumn';
import { calculateCentroid } from '../../../utils/mapHelper';
import { Storage } from '../../../utils/Storage';

import { useAppSelector } from '../../../redux/hook';

// Common Components
import SearchBar from '../../common/SearchBar/SearchBar';
import { useStandardTable as useTable, Table } from '../../common/table/index';

import Map from '../../maps/Map';

// Types
import { Coordinates } from '../../../types/Coordinates';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { getCoordinatesPreset, MapConfig, SearchBarFilterTypes } from '../../../constants/Constants';

// Images
import flagStart from '../../../assets/images/icons/flag-start.png';
import flagFinish from '../../../assets/images/icons/flag-finish.png';
import AuthService from '../../../services/auth.service';
import { AppPermissions } from '../../../constants/Permissions';

interface ProcessSightingProp {
  showMap?: true | undefined;
  accessKey?: string;
  enableDeepLinks?: false | undefined;
  enableExport?: false | undefined;
  onTotalCountChange?: (count: number) => void;
}

export default function ProcessSightings(props: ProcessSightingProp): JSX.Element {
  const { reportId } = useParams<{ reportId: string }>();
  const reportState = useAppSelector((state) => state.readerReports);

  const [showUnpaired, setShowUnpaired] = useState<boolean>(false);
  const [paramFilter, setParamFilter] = useState<any>({
    filters: [{ reportId: reportId, filterType: FilterableTypes.Equals }],
    showUnpaired: false,
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const [positions, setPositions] = useState<Coordinates[]>([]);
  const [center, setCenter] = useState<LatLng>();
  const [zoom, setZoom] = useState<number>(MapConfig.DEFAULT_ZOOM);

  const columns = useMemo(() => makeReaderReportSightingColumn(props.enableDeepLinks !== false), []);

  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getProcessedSightingByReportId,
    exportAsync: props.enableExport !== false ? exportProcessedSightingByReportId : undefined,
    params: paramFilter,
    serverSide: true,
  });

  const refreshData = (searchFilters) => {
    if (!reportState.readerReport?.reportId) return;

    if (!searchFilters) searchFilters = [];

    const filters = [
      ...searchFilters,
      ...paramFilter.filters
        .filter((x) => x.equipmentId !== '' && x.filterType !== FilterableTypes.NotNull)
        .filter((x) => !x.reportId && x.reportId !== undefined),
    ];

    filters.push({
      reportId: reportState.readerReport?.reportId,
      filterType: FilterableTypes.Equals,
    });

    if (!showUnpaired) {
      filters.push({
        equipmentId: '',
        filterType: FilterableTypes.NotNull,
      });
    }

    Storage.setSearchBarFiltering(SearchBarFilterTypes.MOBILE_REPORT, { isMoreFilter: true, filters: filters });

    setParamFilter({ ...paramFilter, filters: filters, lastUpdated: lastUpdated });
  };

  useEffect(() => {
    refreshData(undefined);
  }, [showUnpaired, lastUpdated, reportState.readerReport?.reportId]);

  useEffect(() => {
    const pathLength = reportState.readerReport?.scanSession?.path?.length ?? 0;

    const pos =
      reportState.readerReport?.scanSession?.path?.map((x, idx) => {
        const isStart = idx === 0;
        const isEnd = pathLength > 1 && idx === pathLength - 1;
        return {
          latLng: {
            lat: Number(x.latitude),
            lng: Number(x.longitude),
          },
          showMarker: isStart || isEnd,
          markerIconUrl: isStart ? flagStart : isEnd ? flagFinish : '',
          showPolyline: true,
          markerName: isStart ? 'Start Location' : isEnd ? 'End Location' : '',
        } as Coordinates;
      }) || [];

    if (pos && pos.length) {
      const centroid = calculateCentroid(pos.map((x) => [x.latLng.lng, x.latLng.lat]));
      if (centroid) setCenter(centroid);
      setZoom(MapConfig.DEFAULT_ZOOM);
    } else {
      setZoom(1);
    }

    setPositions(pos);
  }, [reportState.readerReport]);

  useEffect(() => {
    if (!center) return;
    const currentReportId = reportState.readerReport?.reportId;
    if (!currentReportId) return;

    const fetchFacilities = getNearFacilities({ lat: center.lat, lng: center.lng, distanceInMeters: 10000, take: 1000 });
    const fetchNearReaders = getNearReaders({ lat: center.lat, lng: center.lng, distanceInMeters: 10000, filters: [] });
    const fetchGeofences = getGeofences({
      filters: [{ level: 0 }, { stationId: reportState.readerReport?.stationId }],
      skip: 0,
      take: 50,
    });
    const fetchSightings = getProcessedSightingLocationsByReportId(currentReportId) as Promise<any>;

    Promise.all([fetchFacilities, fetchNearReaders, fetchGeofences, fetchSightings]).then(([nearFacilities, nearReaders, geofences, sightings]) => {
      const facilities: Coordinates[] =
        nearFacilities?.data?.results
          ?.filter((v) => v.geofences && v.geofences?.length > 0)
          .map((fa) => ({
            ...getCoordinatesPreset('Facility'),
            latLng: new LatLng(Number(fa.location?.latitude), Number(fa.location?.longitude)),
            geofences: fa.geofences?.map((geof: any) => {
              const geofence = { ...geof };
              // display facilityName from facility.name instead of geofence.facilityName in the map
              geofence.facilityName = fa.name;
              return geofence;
            }),
            showPolygon: true,
          })) || [];

      const readers: Coordinates[] =
        nearReaders?.results
          .filter((n) => n.location && n.intendedUse !== 1 && n.supplyChainStatus === 1)
          .map((r) => ({
            ...getCoordinatesPreset('NearReader'),
            latLng: new LatLng(Number(r.location?.latitude), Number(r.location?.longitude)),
            showMarker: false,
            showCircleMarker: true,
            circleRadius: 280, // Circle radius in meters
          })) || [];

      const airportGeofences: Coordinates[] =
        geofences?.data?.results.map((geof) => ({
          ...getCoordinatesPreset('AirportGeofence'),
          latLng: new LatLng(Number(geof.location?.latitude), Number(geof.location?.longitude)),
          geofences: [geof],
          showPolygon: true,
        })) || [];

      const sightingLocations: Coordinates[] =
        sightings?.data?.map((sighting) => ({
          ...getCoordinatesPreset('Sighting'),
          latLng: new LatLng(sighting.item.latitude, sighting.item.longitude),
          circleRadius: 10,
          showCircleMarker: true,
          markerName: sighting.description,
        })) || [];

      setPositions((prevPositions) => {
        let currentPositions = [...prevPositions];
        currentPositions = currentPositions.filter((v) => v._type !== 'Facility' && v._type !== 'NearReader' && v._type !== 'AirportGeofence');
        currentPositions = [...currentPositions, ...facilities, ...readers, ...airportGeofences, ...sightingLocations];
        return currentPositions;
      });
    });
  }, [center]);

  useEffect(() => {
    if (table?.totalRows !== undefined) {
      props.onTotalCountChange?.(table.totalRows);
    }
  }, [table?.totalRows]);

  return (
    <Paper>
      {center && props.showMap && (
        <Map
          coordinates={positions}
          center={center}
          height={`${Math.min(350, window.innerHeight - 100, window.innerWidth)}px`}
          showToggleButtons={true}
          showToggleButtonCalibration={false}
          aciveToggleTab={['facilities']}
          zoom={zoom}
        />
      )}
      {AuthService.hasPermission(AppPermissions.ReaderReport.Manage) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            maxWidth: 'calc(100% - 32px)',
            minHeight: '30px',
            margin: '12px 0',
            padding: '0 10px',
            background: '#ffffff',
            border: '2px solid #dfe6ef',
            borderRadius: '8px',
            boxSizing: 'border-box',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          <FormControlLabel label='Show Unpaired' control={<Checkbox size='medium' onChange={(event) => setShowUnpaired(event.target.checked)} />} />
        </Box>
      )}
      <SearchBar
        pageKey={SearchBarFilterTypes.MOBILE_REPORT}
        onSearch={(searchFilters) => refreshData(searchFilters)}
        onReset={() => setLastUpdated(new Date())}
        columns={columns}
        allowSearchAll={true}
        components={<div style={{ display: 'flex', marginBottom: 5 }}></div>}
      />
      <Table {...table} showSpinner={true} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
    </Paper>
  );
}
